import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import { useCallback, useMemo } from "react";
import { Field, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { SelectEntity } from "@knowfully/ui.common";
import {
  AgreementRequest,
  AgreementResponse,
  ListResponseOfAgreementStatusResponse,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import { AgreementStatusIds } from "types/common-types";
import { useStrictQuery } from "hooks/useStrictQuery";

type AgreementDetailsFormProps = {
  agreement: AgreementResponse | undefined;
  organizationPickerMode?: ContextOrganizationFieldMode;
  onSubmit: (values: AgreementRequest) => Promise<void>;
  onCancel: () => void;
};

const { Form } = withTypes<AgreementRequest>();

const AgreementDetailsForm = ({
  agreement,
  onSubmit,
  onCancel,
}: AgreementDetailsFormProps) => {
  const { agreementService } = useGlobalContext();

  const { formatMessage: t } = useIntl();

  const init = useMemo(() => {
    const values = agreement
      ? {
          id: agreement.id,
          organizationId: agreement.organizationId,
          name: agreement.name,
          displayName: agreement.displayName,
          agreementStatusId: agreement.agreementStatusId,
          manualReplacements: agreement.manualReplacements,
          templateVersionId: agreement.templateVersionId,
        }
      : {
          agreementStatusId: AgreementStatusIds.Draft.toLowerCase(),
        };

    return AgreementRequest.fromJS(values);
  }, [agreement]);

  const fetchedStatuses = useStrictQuery<ListResponseOfAgreementStatusResponse>(
    ["agreementService.getStatuses"],
    () => agreementService.getStatuses()
  );

  const isDisabled =
    agreement?.agreementStatusId?.toUpperCase() ===
    AgreementStatusIds.Deactivated;

  const isEditable =
    agreement?.agreementStatusId?.toUpperCase() ===
      AgreementStatusIds.Deactivated ||
    agreement?.agreementStatusId?.toUpperCase() === AgreementStatusIds.Active;

  const isNew = agreement?.id == null;

  const statusMap = useMemo<Record<string, string>>(
    () => ({
      [AgreementStatusIds.Active]: "active",
      [AgreementStatusIds.Deactivated]: "deactivated",
      [AgreementStatusIds.Draft]: "draft",
    }),
    []
  );

  const isStatusOptionDisabled = useCallback(
    (optionId: string, currentStatusId?: string, isNew?: boolean): boolean => {
      const value = statusMap[optionId.toUpperCase()];
      const current = statusMap[currentStatusId?.toUpperCase() ?? ""];

      if (isNew) {
        return value !== "draft";
      }

      if (!value || (!current && !isNew)) return false;

      if (current === "draft") {
        return false;
      }

      if (current === "active") {
        return value !== "active" && value !== "deactivated";
      }

      if (current === "deactivated") {
        return value !== "deactivated";
      }

      return false;
    },
    [statusMap]
  );

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({
          handleSubmit,
          submitting,
          submitError,
          values,
          dirty,
          form: { change },
        }) => {
          const requiredField = (value: string | undefined) =>
            value ? undefined : "Required";

          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={change}
                  mode={
                    agreement
                      ? ContextOrganizationFieldMode.EditDisabled
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />

                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        required
                        disabled={isEditable}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="displayName" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Display Name"
                        required
                        disabled={isEditable}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field
                  name="agreementStatusId"
                  required
                  validate={requiredField}
                >
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        variant="standard"
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedStatuses}
                          labelField="name"
                          label={t({ id: "statusLabel" })}
                          {...input}
                          required={true}
                          disabled={isNew}
                          getOptionDisabled={(option) =>
                            isStatusOptionDisabled(
                              option.id,
                              agreement?.agreementStatusId,
                              isNew
                            )
                          }
                          textFieldProps={{
                            variant: "standard",
                            error: meta.error && meta.submitFailed,
                            required: true,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Box marginTop={2} marginLeft={2}>
                    <Alert severity="error">{submitError}</Alert>
                  </Box>
                )}
              </Grid>

              <DialogActions>
                {submitting && <CircularProgress size={24} />}
                <Button disabled={submitting || !dirty} onClick={onCancel}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  disabled={submitting || !dirty || isDisabled}
                >
                  {t({ id: "saveButton" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    ),
    [
      agreement,
      fetchedStatuses,
      init,
      isDisabled,
      isEditable,
      isNew,
      isStatusOptionDisabled,
      onCancel,
      onSubmit,
      t,
    ]
  );
};

export default AgreementDetailsForm;
